// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import type { ChangeFeedStartFrom } from "./ChangeFeedStartFrom.js";
import type { ChangeFeedMode } from "./ChangeFeedMode.js";
import type { PriorityLevel } from "../../documents/PriorityLevel.js";
/**
 * Specifies options for the change feed
 *
 * If none of those options are set, it will start reading changes from now for the entire container.
 */
export interface ChangeFeedIteratorOptions {
  /**
   * Max amount of items to return per page
   */
  maxItemCount?: number;
  /**
   * The session token to use. If not specified, will use the most recent captured session token to start with.
   */
  sessionToken?: string;
  /**
   * Signals where to start from in the change feed.
   */
  changeFeedStartFrom?: ChangeFeedStartFrom;
  /**
   * Signals the mode in which the change feed needs to start.
   */
  changeFeedMode?: ChangeFeedMode;

  /**
   * Excludes one or more Azure regions for the operation.
   * <p>This option is only applied when enableEndPointDiscovery is set to true.</p>
   */
  excludedLocations?: string[];

  /**
   * Priority Level (Low/High) for each request.
   * Low priority requests are always throttled before any high priority requests.
   *
   * <p>Default value is null. By default all requests are of High priority</p>
   */
  priorityLevel?: PriorityLevel;
  /**
   * Throughput Bucket for a request.
   *
   * <p>Default value is null. In this case, the request can use 100% of the partition throughput. </p>
   * For more information, visit [Cosmos DB throughput Bucketing](https://aka.ms/cosmsodb-bucketing).
   */
  throughputBucket?: number;
}
